import { SignaturePiece } from '../signatures/SignaturesPage.js';
import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Dialog, Input, Textarea, Checkbox, Select } from '@artosera/ui';
import {
  useTousContratsArtistes,
  useCreerContratArtiste,
  useModifierContratArtiste,
  useGesteContratArtiste,
  type ContratArtistePresente,
} from '../../api/contratsArtistes.js';
import { useContrats } from '../../api/contrats.js';
import { coffreApi } from '../../api/coffre.js';
import { cleMetier, appelDeRequete } from '../../api/cles.js';
import { Field } from '../Field.js';
import { dateContrat, erreurContrat } from '../contrats/contratUi.js';
import { estApiError } from '../../api/errors.js';
import styles from '../contrats/ContratsPage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';
export function ContratRepresentationSection({
  galerieSlug,
  artisteId,
  peutEcrire,
  avecCoffre = false,
  avecModeles = false,
  avecSignature = false,
}: {
  galerieSlug: string;
  artisteId: string;
  peutEcrire: boolean;
  avecCoffre?: boolean;
  avecModeles?: boolean;
  avecSignature?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const label = (s: string) => t(`regie:contratsArtistes.${s}`);
  const liste = useTousContratsArtistes(galerieSlug, { artisteId });
  const creer = useCreerContratArtiste(galerieSlug);
  const modifier = useModifierContratArtiste(galerieSlug);
  const geste = useGesteContratArtiste(galerieSlug);
  const [acte, setActe] = useState<{
    type: 'nouveau' | 'vigueur' | 'renouveler' | 'resilier' | 'document' | 'contrat-genere';
    contrat?: ContratArtistePresente;
  } | null>(null);
  const [debut, setDebut] = useState(new Date().toISOString().slice(0, 10));
  const [fin, setFin] = useState('');
  const [exclusif, setExclusif] = useState(false);
  const [commission, setCommission] = useState('');
  const [territoire, setTerritoire] = useState('');
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const [documentConflit, setDocumentConflit] = useState<string | null>(null);
  const docs = useQuery({
    queryKey: cleMetier(galerieSlug, 'coffre', 'documents', 'contrat-artiste', artisteId),
    queryFn: (c) =>
      coffreApi.lister(
        galerieSlug,
        { rattache: `artiste:${artisteId}`, type: ['contrat_representation'], parPage: 200 },
        appelDeRequete(c),
      ),
    enabled: avecCoffre && acte?.type === 'document',
  });
  const generes = useContrats(
    galerieSlug,
    { artisteId, genre: 'artiste', parPage: 200 },
    avecModeles && acte?.type === 'contrat-genere',
  );
  const ouvrir = (type: NonNullable<typeof acte>['type'], contrat?: ContratArtistePresente) => {
    setErreur('');
    setDocumentConflit(null);
    setMotif('');
    setFin(contrat?.finLe?.slice(0, 10) ?? '');
    setDebut(new Date().toISOString().slice(0, 10));
    setActe({ type, ...(contrat ? { contrat } : {}) });
  };
  async function faire(fn: () => Promise<unknown>) {
    setErreur('');
    setDocumentConflit(null);
    try {
      await fn();
      setActe(null);
    } catch (e) {
      if (
        estApiError(e) &&
        typeof e.details === 'object' &&
        e.details !== null &&
        'raison' in e.details &&
        e.details.raison === 'echeance_portee_par_le_coffre'
      ) {
        setErreur(label('echeanceCoffre'));
        if ('documentId' in e.details && typeof e.details.documentId === 'string')
          setDocumentConflit(e.details.documentId);
      } else setErreur(erreurContrat(e, t('commun:erreurReseau')));
    }
  }
  async function soumettre() {
    if (!acte) return;
    const c = acte.contrat;
    if (acte.type === 'nouveau')
      await faire(() =>
        creer.mutateAsync({
          artisteId,
          debutLe: debut,
          finLe: fin || null,
          exclusivite: exclusif,
          commission,
          territoire,
        }),
      );
    else if (c && acte.type === 'renouveler')
      await faire(() => modifier.mutateAsync({ id: c.id, corps: { finLe: fin || null } }));
    else if (c && acte.type === 'vigueur')
      await faire(() => geste.mutateAsync({ id: c.id, geste: 'vigueur' }));
    else if (c && acte.type === 'resilier')
      await faire(() =>
        geste.mutateAsync({ id: c.id, geste: 'resilier', motif, resilieLe: debut }),
      );
  }
  const contrats = [...(liste.data ?? [])].sort(
    (a, b) =>
      (a.etat === 'en_vigueur' ? -1 : 1) - (b.etat === 'en_vigueur' ? -1 : 1) ||
      b.debutLe.localeCompare(a.debutLe),
  );
  const afficher = (c: ContratArtistePresente) => (
    <article key={c.id} className={styles.article}>
      <div className={styles.actions}>
        <Badge tone={c.etat === 'en_vigueur' ? 'accent' : 'neutral'}>
          {label(`etats.${c.etat}`)}
        </Badge>
        <span>
          {label('debut')} : {dateContrat(c.debutLe, i18n.language)}
        </span>
      </div>
      <p>
        {c.finLe
          ? c.echu
            ? label('echuDepuis') + ' ' + dateContrat(c.finLe, i18n.language)
            : label('echeance') +
              ' ' +
              dateContrat(c.finLe, i18n.language) +
              ' · ' +
              t('regie:contratsArtistes.dansJours', { count: c.joursAvantEcheance ?? 0 })
          : label('sansEcheance')}
      </p>
      <dl>
        <dt>{label('exclusivite')}</dt>
        <dd>{c.exclusivite ? t('commun:oui') : t('commun:non')}</dd>
        <dt>{label('commission')}</dt>
        <dd>{c.commission || '—'}</dd>
        <dt>{label('territoire')}</dt>
        <dd>{c.territoire || '—'}</dd>
      </dl>
      {c.documentId && avecCoffre && (
        <p>
          <Link to={`/${galerieSlug}/coffre/${c.documentId}`}>{label('documentLie')}</Link>
        </p>
      )}
      {c.contratGenereId && avecModeles && (
        <p>
          <Link to={`/${galerieSlug}/contrats`}>{label('contratGenereLie')}</Link>
        </p>
      )}
      {avecSignature && c.contratGenereId && (
        <SignaturePiece source={{ type: 'contrat_artiste', id: c.id }} />
      )}
      {c.resilieLe && (
        <p>
          {label('dateResiliation')} : {dateContrat(c.resilieLe, i18n.language)} ·{' '}
          {c.motifResiliation}
        </p>
      )}
      {peutEcrire && c.etat !== 'resilie' && (
        <div className={styles.actions}>
          {c.etat === 'brouillon' ? (
            <Button size="sm" onClick={() => ouvrir('vigueur', c)}>
              {label('mettreEnVigueur')}
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={() => ouvrir('renouveler', c)}>
                {label('renouveler')}
              </Button>
              <Button variant="danger" size="sm" onClick={() => ouvrir('resilier', c)}>
                {label('resilier')}
              </Button>
            </>
          )}
          {avecCoffre && (
            <Button variant="secondary" size="sm" onClick={() => ouvrir('document', c)}>
              {label('lierDocument')}
            </Button>
          )}
          {avecModeles && (
            <Button variant="secondary" size="sm" onClick={() => ouvrir('contrat-genere', c)}>
              {label('lierContratGenere')}
            </Button>
          )}
        </div>
      )}
    </article>
  );
  return (
    <section className={styles.form}>
      <header className={styles.entete}>
        <h2>{label('titre')}</h2>
        <Verrouillable action="artist-contracts:write" ouvert={peutEcrire}>
          <Button size="sm" onClick={() => ouvrir('nouveau')}>
            {label('nouveau')}
          </Button>
        </Verrouillable>
      </header>
      {liste.isPending && <p role="status">{t('commun:chargement')}</p>}
      {liste.isError && <p role="alert">{erreurContrat(liste.error, t('commun:erreurReseau'))}</p>}
      {liste.data?.length === 0 && <p>{label('aucun')}</p>}
      {contrats.filter((c) => c.etat !== 'resilie').map(afficher)}
      {contrats.some((c) => c.etat === 'resilie') && (
        <details>
          <summary>{label('historique')}</summary>
          {contrats.filter((c) => c.etat === 'resilie').map(afficher)}
        </details>
      )}
      <Dialog
        open={acte !== null}
        onOpenChange={(v) => {
          if (!v) setActe(null);
        }}
        title={label(
          acte?.type === 'vigueur'
            ? 'mettreEnVigueur'
            : acte?.type === 'document'
              ? 'lierDocument'
              : acte?.type === 'contrat-genere'
                ? 'lierContratGenere'
                : (acte?.type ?? 'nouveau'),
        )}
        closeLabel={t('commun:actions.fermer')}
      >
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void soumettre();
          }}
        >
          {acte?.type === 'vigueur' && <p>{label('avertissementResume')}</p>}
          {(acte?.type === 'nouveau' || acte?.type === 'resilier') && (
            <Field
              htmlFor="artist-contract-debut"
              label={label(acte.type === 'resilier' ? 'dateResiliation' : 'debut')}
            >
              <Input
                id="artist-contract-debut"
                type="date"
                required
                value={debut}
                onChange={(e) => setDebut(e.target.value)}
              />
            </Field>
          )}
          {(acte?.type === 'nouveau' || acte?.type === 'renouveler') && (
            <Field
              htmlFor="artist-contract-fin"
              label={label(acte.type === 'renouveler' ? 'nouvelleEcheance' : 'echeance')}
            >
              <Input
                id="artist-contract-fin"
                type="date"
                min={acte.type === 'nouveau' ? debut : acte.contrat?.debutLe.slice(0, 10)}
                value={fin}
                onChange={(e) => setFin(e.target.value)}
              />
            </Field>
          )}
          {acte?.type === 'nouveau' && (
            <>
              <Checkbox
                id="artist-contract-exclusif"
                label={label('exclusivite')}
                checked={exclusif}
                onCheckedChange={(v) => setExclusif(v === true)}
              />
              <Field htmlFor="artist-contract-commission" label={label('commission')}>
                <Input
                  id="artist-contract-commission"
                  value={commission}
                  onChange={(e) => setCommission(e.target.value)}
                />
              </Field>
              <Field htmlFor="artist-contract-territoire" label={label('territoire')}>
                <Input
                  id="artist-contract-territoire"
                  value={territoire}
                  onChange={(e) => setTerritoire(e.target.value)}
                />
              </Field>
            </>
          )}
          {acte?.type === 'resilier' && (
            <Field htmlFor="artist-contract-motif" label={label('motif')}>
              <Textarea
                id="artist-contract-motif"
                required
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
              />
            </Field>
          )}
          {acte?.type === 'document' && (
            <>
              {docs.isPending && <p role="status">{t('commun:chargement')}</p>}
              {docs.isError && (
                <p role="alert">{erreurContrat(docs.error, t('commun:erreurReseau'))}</p>
              )}
              <Select
                aria-label={label('lierDocument')}
                value={acte.contrat?.documentId ?? 'aucun'}
                onValueChange={(v) => {
                  if (acte.contrat)
                    void faire(() =>
                      geste.mutateAsync({
                        id: acte.contrat!.id,
                        geste: 'document',
                        documentId: v === 'aucun' ? null : v,
                      }),
                    );
                }}
                options={[
                  { value: 'aucun', label: label('sansDocument') },
                  ...(docs.data?.documents ?? []).map((d) => ({ value: d.id, label: d.titre })),
                ]}
              />
            </>
          )}
          {acte?.type === 'contrat-genere' && (
            <>
              {generes.isError && (
                <p role="alert">{erreurContrat(generes.error, t('commun:erreurReseau'))}</p>
              )}
              <Select
                aria-label={label('lierContratGenere')}
                value={acte.contrat?.contratGenereId ?? 'aucun'}
                onValueChange={(v) => {
                  if (acte.contrat)
                    void faire(() =>
                      geste.mutateAsync({
                        id: acte.contrat!.id,
                        geste: 'contrat-genere',
                        contratId: v === 'aucun' ? null : v,
                      }),
                    );
                }}
                options={[
                  { value: 'aucun', label: label('sansContratGenere') },
                  ...(generes.data?.contrats ?? [])
                    .filter((c) => c.etat !== 'annule')
                    .map((c) => ({ value: c.id, label: `${c.numero} — ${c.titre}` })),
                ]}
              />
            </>
          )}
          {erreur && <p role="alert">{erreur}</p>}
          {documentConflit && (
            <Link to={`/${galerieSlug}/coffre/${documentConflit}`}>{label('voirDocument')}</Link>
          )}
          {acte?.type !== 'document' && acte?.type !== 'contrat-genere' && (
            <Button
              type="submit"
              loading={creer.isPending || modifier.isPending || geste.isPending}
              disabled={acte?.type === 'resilier' && !motif.trim()}
            >
              {label(acte?.type === 'vigueur' ? 'mettreEnVigueur' : (acte?.type ?? 'nouveau'))}
            </Button>
          )}
        </form>
      </Dialog>
    </section>
  );
}
